'use client';

import { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Icon } from './Icon';
import { useSatelliteNavigationOptional } from '@to/ui';

export interface VendorFilterSidebarProps {
  categoryCounts: Record<string, number>;
  locationCounts: Record<string, number>;
  totalVendors: number;
  /** Base path for vendor URLs (e.g., '/vendors' or '/star3/vendors'). Default: '/vendors' */
  vendorsPath?: string;
}

export function VendorFilterSidebar({
  categoryCounts,
  locationCounts,
  totalVendors,
  vendorsPath = '/vendors',
}: VendorFilterSidebarProps) {
  const router = useRouter();
  const navigation = useSatelliteNavigationOptional();
  const searchParams = useSearchParams();
  const [locationsExpanded, setLocationsExpanded] = useState(false);
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);

  const selectedCategories = searchParams.getAll('category');
  const selectedLocations = searchParams.getAll('location');

  const sortedCategories = Object.entries(categoryCounts).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const sortedLocations = Object.entries(locationCounts).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  const totalLocationVendors = Object.values(locationCounts).reduce(
    (sum, count) => sum + count,
    0,
  );
  const totalCategoryVendors = Object.values(categoryCounts).reduce(
    (sum, count) => sum + count,
    0,
  );

  function buildUrl(newCategories: string[], newLocations: string[]): string {
    const params = new URLSearchParams();
    const agent = searchParams.get('agent');
    if (agent) params.set('agent', agent);
    const q = searchParams.get('q');
    if (q) params.set('q', q);
    for (const cat of newCategories) params.append('category', cat);
    for (const loc of newLocations) params.append('location', loc);
    const sort = searchParams.get('sort');
    if (sort) params.set('sort', sort);
    const qs = params.toString();
    return qs ? `${vendorsPath}?${qs}` : vendorsPath;
  }

  function navigate(targetHref: string) {
    const started =
      navigation?.beginNavigation({
        routeTemplate: '/vendors',
        navigationType: 'data_action',
        targetKey: targetHref,
        targetHref,
      }) ?? true;
    if (started) router.push(targetHref);
  }

  function handleCategoryChange(categoryName: string, checked: boolean) {
    const newCategories = checked
      ? [...selectedCategories, categoryName]
      : selectedCategories.filter((c) => c !== categoryName);
    navigate(buildUrl(newCategories, selectedLocations));
  }

  function handleLocationChange(locationName: string, checked: boolean) {
    const newLocations = checked
      ? [...selectedLocations, locationName]
      : selectedLocations.filter((l) => l !== locationName);
    navigate(buildUrl(selectedCategories, newLocations));
  }

  function handleAllLocations() {
    if (selectedLocations.length > 0) {
      navigate(buildUrl(selectedCategories, []));
    }
  }

  function handleAllCategories() {
    if (selectedCategories.length > 0) {
      navigate(buildUrl([], selectedLocations));
    }
  }

  const hasFilters =
    selectedCategories.length > 0 ||
    selectedLocations.length > 0 ||
    searchParams.has('q');

  return (
    <aside
      className="w-full rounded-xl border p-5"
      style={{
        borderColor: 'var(--brand-card-border)',
        backgroundColor: 'var(--brand-card-bg)',
        fontFamily: 'var(--brand-font-body)',
      }}
    >
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h2
          className="text-base font-semibold"
          style={{ color: 'var(--brand-text)', fontFamily: 'var(--brand-font-heading)' }}
        >
          Filter Vendors
        </h2>
        {hasFilters && (
          <Link
            href={`${vendorsPath}${searchParams.get('agent') ? `?agent=${encodeURIComponent(searchParams.get('agent')!)}` : ''}`}
            className="text-sm font-medium transition-opacity hover:opacity-80"
            style={{ color: 'var(--brand-primary-text, var(--brand-primary))' }}
          >
            Clear Filters
          </Link>
        )}
      </div>

      {/* Service Areas Section */}
      <div
        className="border-t pt-4"
        style={{ borderColor: 'var(--brand-card-border)' }}
      >
        <button
          onClick={() => setLocationsExpanded(!locationsExpanded)}
          className="mb-3 flex w-full items-center gap-2 text-left"
          aria-expanded={locationsExpanded}
        >
          {locationsExpanded ? (
            <Icon
              name="expand_more"
              size={16}
              style={{ color: 'var(--brand-text-muted)' }}
            />
          ) : (
            <Icon
              name="chevron_right"
              size={16}
              style={{ color: 'var(--brand-text-muted)' }}
            />
          )}
          <span
            className="text-sm font-semibold"
            style={{ color: 'var(--brand-text)', fontFamily: 'var(--brand-font-heading)' }}
          >
            Service Areas
          </span>
        </button>

        {locationsExpanded && (
          <div className="flex flex-col gap-2 pl-1">
            {/* All option */}
            <label className="flex min-h-6 cursor-pointer items-center gap-2.5">
              <input
                type="checkbox"
                disabled={navigation?.isPending}
                checked={selectedLocations.length === 0}
                onChange={handleAllLocations}
                className="h-4 w-4 rounded"
                style={{ accentColor: 'var(--brand-primary, #014F8A)' }}
              />
              <span
                className="text-sm"
                style={{ color: 'var(--brand-text)' }}
              >
                All ({totalVendors})
              </span>
            </label>

            {sortedLocations.map(([name, count]) => (
              <label
                key={name}
                className="flex min-h-6 cursor-pointer items-center gap-2.5"
              >
                <input
                  type="checkbox"
                  disabled={navigation?.isPending}
                  checked={selectedLocations.includes(name)}
                  onChange={(e) =>
                    handleLocationChange(name, e.target.checked)
                  }
                  className="h-4 w-4 rounded"
                  style={{ accentColor: 'var(--brand-primary, #014F8A)' }}
                />
                <span
                  className="text-sm"
                  style={{ color: 'var(--brand-text)' }}
                >
                  {name} ({count})
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Categories Section */}
      <div
        className="mt-4 border-t pt-4"
        style={{ borderColor: 'var(--brand-card-border)' }}
      >
        <button
          onClick={() => setCategoriesExpanded(!categoriesExpanded)}
          className="mb-3 flex w-full items-center gap-2 text-left"
          aria-expanded={categoriesExpanded}
        >
          {categoriesExpanded ? (
            <Icon
              name="expand_more"
              size={16}
              style={{ color: 'var(--brand-text-muted)' }}
            />
          ) : (
            <Icon
              name="chevron_right"
              size={16}
              style={{ color: 'var(--brand-text-muted)' }}
            />
          )}
          <span
            className="text-sm font-semibold"
            style={{ color: 'var(--brand-text)', fontFamily: 'var(--brand-font-heading)' }}
          >
            Categories
          </span>
        </button>

        {categoriesExpanded && (
          <div className="flex flex-col gap-2 pl-1">
            {/* All option */}
            <label className="flex min-h-6 cursor-pointer items-center gap-2.5">
              <input
                type="checkbox"
                disabled={navigation?.isPending}
                checked={selectedCategories.length === 0}
                onChange={handleAllCategories}
                className="h-4 w-4 rounded"
                style={{ accentColor: 'var(--brand-primary, #014F8A)' }}
              />
              <span
                className="text-sm"
                style={{ color: 'var(--brand-text)' }}
              >
                All ({totalVendors})
              </span>
            </label>

            {sortedCategories.map(([name, count]) => (
              <label
                key={name}
                className="flex min-h-6 cursor-pointer items-center gap-2.5"
              >
                <input
                  type="checkbox"
                  disabled={navigation?.isPending}
                  checked={selectedCategories.includes(name)}
                  onChange={(e) =>
                    handleCategoryChange(name, e.target.checked)
                  }
                  className="h-4 w-4 rounded"
                  style={{ accentColor: 'var(--brand-primary, #014F8A)' }}
                />
                <span
                  className="text-sm"
                  style={{ color: 'var(--brand-text)' }}
                >
                  {name} ({count})
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
